import { useQuery, useQueryClient, Query } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useCredits } from '@/contexts/CreditContext';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  TrendingUp,
  Eye,
  BarChart3,
  FileText,
  Loader2, // Added for loading state
  Video,
  Instagram,
  Youtube,
  CreditCard,
  Smartphone, // Added for Shorts
} from 'lucide-react';
import { formatDate, formatNumber } from '@/lib/utils';
import { api } from '@/lib/api';
import { TikTokIcon } from '@/components/icons/TikTokIcon';
import { useState, useEffect } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { CreditSummaryCard } from '@/components/CreditSummaryCard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import React from 'react';

// Add this utility function near the top
const toCamelCase = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(v => toCamelCase(v));
  } else if (obj !== null && obj !== undefined && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = key.replace(/_([a-z])/g, g => g[1].toUpperCase());
      result[camelKey] = toCamelCase(obj[key]);
      return result;
    }, {} as any);
  }
  return obj;
};

interface ContentItem {
  id: string;
  title: string | null;
  platform: string;
  status: string;
  views: number;
  engagement: number;
  createdAt: string;
  scheduledAt: string | null;
  topic?: string;
  customPrompt?: string;
  topicName?: string;
  metrics: {
    views: number;
    engagement: number;
  };
}

interface DashboardResponse {
  items: ContentItem[];
  total: number;
  currentPage: number;
  totalPages: number;
}

const isVerticalOrientation = (platform: string): boolean => {
  return ['tiktok', 'instagram', 'shorts'].includes(platform.toLowerCase());
};

const formatPlatform = (platform: string): string => {
  const platforms: { [key: string]: string } = {
    'tiktok': 'TikTok',
    'instagram': 'Instagram',
    'shorts': 'YouTube Shorts',
    'youtube': 'YouTube',
    'video': 'Video'
  };
  return platforms[platform.toLowerCase()] || platform;
};

const formatStatus = (status: string): string => {
  const statusLower = status.toLowerCase();
  if (['in_progress', 'processing', 'generating'].includes(statusLower)) {
    return 'Processing (~5 mins)';
  }
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

// Add this component for the coming soon overlay
function ComingSoonOverlay() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-lg backdrop-blur-[1px]">
      <span className="text-sm font-medium text-muted-foreground">Coming Soon</span>
    </div>
  );
}

export function Dashboard() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { creditInfo } = useCredits();

  // Check if we're coming from content creation
  const isFromContentCreation = location.state?.from === 'content-creation';

  const { data = { items: [], total: 0, currentPage: 1, totalPages: 1 }, isLoading, refetch } = useQuery<DashboardResponse>({
    queryKey: ['content', currentPage],
    queryFn: async () => {
      const response = await api.get(`/content/user-content?page=${currentPage}&page_size=${pageSize}`);
      return toCamelCase(response.data) as DashboardResponse;
    },
    staleTime: 5000,
    gcTime: 60000,
    refetchOnWindowFocus: true
  });

  // Force refetch when coming from content creation
  useEffect(() => {
    if (isFromContentCreation) {
      queryClient.invalidateQueries({ queryKey: ['content'] });
      // Clear the navigation state
      navigate(location.pathname, { replace: true });
    }
  }, [isFromContentCreation, queryClient, navigate, location.pathname]);

  console.log('Pagination Data:', {
    total: data?.total,
    currentPage: data?.currentPage,
    totalPages: data?.totalPages,
    hasItems: Boolean(data?.items?.length)
  });

  const handleEdit = (item: ContentItem) => {
    navigate(`/edit/${item.id}`);
  };

  const handleViewStoryboard = (item: ContentItem) => {
    navigate(`/storyboard/${item.id}`);
  };

  const handleDelete = async (id: string) => {
    // Store the current content data before deletion
    const previousContent = queryClient.getQueryData<ContentItem[]>(['content']);

    try {
      // Optimistically remove the item from the UI
      queryClient.setQueryData<ContentItem[]>(['content'], (old: ContentItem[] | undefined) => 
        old?.filter((item: ContentItem) => item.id !== id) ?? []
      );

      // Use the configured api client instead of fetch
      await api.delete(`/content/${id}`);

      // Refetch to ensure data consistency
      await queryClient.invalidateQueries({ queryKey: ['content'] });
      
      toast({
        title: "Content deleted",
        description: "The content has been successfully deleted.",
      });

    } catch (error: any) {
      // Revert optimistic update on error
      if (previousContent) {
        queryClient.setQueryData(['content'], previousContent);
      }
      
      // Check if the error is a 404 (content already deleted)
      if (error.response?.status === 404) {
        // Content already deleted, just invalidate queries to refresh the list
        await queryClient.invalidateQueries({ queryKey: ['content'] });
        return;
      }
      
      console.error('Error deleting content:', error);
      toast({
        title: "Error",
        description: "Failed to delete content. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (['in_progress', 'processing', 'generating'].includes(statusLower)) {
      return 'bg-yellow-100 text-yellow-800 whitespace-nowrap';
    }
    switch (statusLower) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'awaiting_approval':
      case 'pending_review':
        return 'bg-orange-100 text-orange-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handlePageChange = (newPage: number) => {
    console.log('Changing to page:', newPage);
    if (newPage >= 1 && newPage <= Math.ceil((data?.total || 0) / pageSize)) {
      setCurrentPage(newPage);
    }
  };

  // Add this function near the top of your component to generate the rotating tips
  function getTipMessage() {
    const tips = [
      "Pro tip: Use descriptive titles for better discovery",
      "Short videos (15-30s) tend to perform better",
      "Try different platforms to reach new audiences",
      "Creating your next viral hit? We believe in you!",
      "Remember to engage with comments on your uploads",
      "Consistent posting increases audience growth"
    ];
    
    // This ensures the message stays the same during a session
    // but changes between sessions
    const date = new Date();
    const startOfYear = new Date(date.getFullYear(), 0, 0);
    const dayOfYear = Math.floor(
      (date.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24)
    );
    const tipIndex = dayOfYear % tips.length;
    
    return tips[tipIndex];
  }

  return (
    <div className="container py-6 md:py-10">
      <div className="flex flex-col space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-1">Content Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Manage and track your content performance
          </p>
        </div>

        {/* Analytics Strip */}
        <div className="flex flex-wrap md:flex-nowrap md:items-center md:justify-between pl-4">
          <div className="grid grid-cols-2 w-full md:w-auto md:flex md:items-center gap-4 md:gap-6">
            <div className="flex flex-col">
              <div className="flex items-center text-sm text-muted-foreground mb-1">
                <CreditCard className="h-4 w-4 mr-1.5" />
                <span>Credits</span>
              </div>
              <div className="font-medium">{creditInfo?.available_credits || 0} available</div>
            </div>

            <div className="border-l border-border pl-4 flex flex-col">
              <div className="flex items-center text-sm text-muted-foreground mb-1">
                <FileText className="h-4 w-4 mr-1.5" />
                <span>Content</span>
              </div>
              <div className="font-medium">{data?.total ? `${data.total} created` : 'None created'}</div>
            </div>

            <div className="hidden md:flex md:flex-col md:border-l border-border px-6">
              <div className="flex items-center text-sm text-muted-foreground mb-1">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 mr-1.5" />
                <span>Beta Release</span>
              </div>
              <div className="font-medium text-sm text-amber-700">
                <a href="mailto:info@adeystudios.com" className="hover:underline">
                  Report issues
                </a>
              </div>
            </div>
          </div>
          
          <div className="hidden md:flex items-center pr-4">
            <TrendingUp className="h-3.5 w-3.5 mr-1.5 text-primary" />
            <span className="text-sm text-muted-foreground">{getTipMessage()}</span>
          </div>
        </div>

        {/* Content List */}
        <Card className="mt-6 border-t-2 border-t-primary/20">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">Recent Content</CardTitle>
                <CardDescription>
                  View and manage your content
                </CardDescription>
              </div>
              <Button 
                onClick={() => navigate('/create')} 
                size="icon"
                className="rounded-full h-10 w-10"
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : data?.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground text-base">
                  No content yet
                </p>
                <p className="text-sm text-muted-foreground/80 text-center max-w-[300px] mt-2">
                  Start creating your first piece of content by clicking the plus button above
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <AnimatePresence>
                  {data.items.map((item: ContentItem) => (
                    <motion.div
                      key={item.id}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="group flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                      onClick={() => handleViewStoryboard(item)}
                    >
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <h3 className="text-sm md:text-base font-medium truncate">
                          {item.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2.5 text-muted-foreground">
                          {item.platform.toLowerCase() === 'youtube' && <Youtube className="h-3.5 w-3.5 md:h-4 md:w-4 shrink-0" />}
                          {item.platform.toLowerCase() === 'tiktok' && <TikTokIcon className="h-3.5 w-3.5 md:h-4 md:w-4 shrink-0" />}
                          {item.platform.toLowerCase() === 'shorts' && <Smartphone className="h-3.5 w-3.5 md:h-4 md:w-4 shrink-0" />}
                          {item.platform.toLowerCase() === 'instagram' && <Instagram className="h-3.5 w-3.5 md:h-4 md:w-4 shrink-0" />}
                          {item.platform.toLowerCase() === 'video' && <Video className="h-3.5 w-3.5 md:h-4 md:w-4 shrink-0" />}
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] md:text-xs ${getStatusColor(item.status)}`}>
                            {formatStatus(item.status)}
                          </span>
                          <span className="text-xs">
                            {item.scheduledAt ? formatDate(item.scheduledAt) : 'No scheduled date'}
                          </span>
                        </div>
                      </div>

                      {/* Action buttons - desktop only */}
                      <div className="hidden md:flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(item);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-red-600"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. This will permanently delete your content.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(item.id);
                                }}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>

                      {/* More options menu - mobile only */}
                      <div className="md:hidden">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-32">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(item);
                            }}>
                              <Pencil className="mr-2 h-4 w-4" />
                              <span className="text-sm">Edit</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(item.id);
                              }}
                              className="text-red-600 cursor-pointer"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              <span className="text-sm">Delete</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Pagination - only show if there are items and more than one page */}
                {data.total > pageSize && (
                  <div className="flex items-center justify-between md:justify-center gap-3 pt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="h-8 px-2 text-sm"
                    >
                      Previous
                    </Button>

                    <div className="hidden md:flex gap-1">
                      {Array.from({ length: Math.ceil(data.total / pageSize) }, (_, i) => i + 1).map((pageNum) => (
                        <Button
                          key={pageNum}
                          variant={pageNum === currentPage ? "default" : "ghost"}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          className="h-8 w-8 px-0"
                        >
                          {pageNum}
                        </Button>
                      ))}
                    </div>

                    <span className="md:hidden text-xs text-muted-foreground">
                      Page {currentPage}
                    </span>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= Math.ceil(data.total / pageSize)}
                      className="h-8 px-2 text-sm"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export { Dashboard as DashboardPage };
